<template>
  <div class="question-list">
    <el-table
      ref="table"
      v-loading="loading"
      :data="localQuestions"
      border
      stripe
      class="inner-table"
      style="width: 100%">
      
      <el-table-column
        prop="question_seq"
        label="题号"
        width="70"
        align="center">
      </el-table-column>
      
      <el-table-column
        prop="question_name"
        label="题目名称"
        width="220"
        align="left">
        <template slot-scope="scope">
          <div class="question-name-cell">
            <span class="question-name">
              <a
                v-if="scope.row.question_url"
                :href="scope.row.question_url"
                target="_blank"
                class="name-link"
              >
                {{ scope.row.question_name }}
              </a>
              <span v-else>{{ scope.row.question_name }}</span>
            </span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="question_score"
        label="得分"
        width="80"
        align="right">
        <template slot-scope="scope">
          <div class="score-cell">
            <i v-if="scope.row.question_score === 100" 
               class="el-icon-medal-1" 
               :style="{ color: getScoreColor(scope.row.question_score), marginRight: '4px' }"></i>
            <span :style="{ 
              color: getScoreColor(scope.row.question_score),
              fontWeight: 'bold'
            }">
              {{ scope.row.question_score }}
            </span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="question_score_fix"
        label="订正分数"
        width="80"
        align="right">
        <template slot-scope="scope">
          <div class="score-cell">
            <i v-if="scope.row.question_score_fix === 100" 
               class="el-icon-medal-1" 
               :style="{ color: getScoreColor(scope.row.question_score_fix), marginRight: '4px' }"></i>
            <span :style="{ 
              color: getScoreColor(scope.row.question_score_fix),
              fontWeight: 'bold'
            }">
              {{ scope.row.question_score_fix }}
            </span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        prop="question_level"
        label="题目难度"
        width="100"
        align="center">
        <template slot-scope="scope">
          <span :style="getQuestionLevelStyle(scope.row.question_level)">
            {{ getQuestionLevelLabel(scope.row.question_level) }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        label="错误类型"
        min-width="140"
        align="left">
        <template slot="header" slot-scope="scope">
          <div style="display: flex; align-items: center;">
            <span>错误类型</span>
            <el-tooltip effect="dark" placement="top" popper-class="difficulty-tooltip">
              <div slot="content" class="difficulty-tooltip-content">
                <div class="difficulty-description">
                  <p><strong>1. 算法未掌握</strong>：未掌握核心知识点、算法原理或其适用性；</p>
                  <ul>
                    <li>【学透】深入理解定义、原理、推导、复杂度</li>
                    <li>【对比】区分相似概念，明确应用场景</li>
                    <li>【复习】定期回顾基础知识</li>
                  </ul>
                  <p><strong>2. 思路/逻辑偏差</strong>：思路框架或解题逻辑有缺陷，无法构建正确解题路径；</p>
                  <ul>
                    <li>【拆解】将问题分解，逐个击破</li>
                    <li>【模拟】用小数据手动演算，验证思路</li>
                    <li>【学习】学习他人优秀解法，寻找更优思路</li>
                  </ul>
                  <p><strong>3. 实现/细节错误</strong>：思路正确，但代码细节处理不当，导致程序出错；</p>
                  <ul>
                    <li>【细抠】关注边界、初始化、数据类型、语句逻辑</li>
                    <li>【调试】熟练使用调试器，定位问题</li>
                    <li>【规范】养成良好的编码习惯</li>
                  </ul>
                  <p><strong>4. 效率/复杂度问题</strong>：算法或实现效率低，导致超时 (TLE) 或超内存 (MLE)；</p>
                  <ul>
                    <li>【估算】优先评估算法时间/空间复杂度</li>
                    <li>【优化】学习常用优化技巧（如剪枝、数据结构）</li>
                    <li>【卡点】针对性练习时间/空间敏感题</li>
                  </ul>
                  <p><strong>5. 边界/特殊情况遗漏</strong>：未考虑题目中的特殊数据、极端情况或边界条件；</p>
                  <ul>
                    <li>【预判】思考各种可能的小数据、负数、空集、全同等情况</li>
                    <li>【测试】编写专门测试边界的用例</li>
                    <li>【总结】建立边界条件检查清单</li>
                  </ul>
                  <p><strong>6. 题意理解偏差</strong>：对题目描述、要求、输入输出等理解有误；</p>
                  <ul>
                    <li>【精读】慢读、细读，划出关键词</li>
                    <li>【明确】确认输入输出格式、数据范围、约束条件</li>
                    <li>【举例】用自己的话复述题目</li>
                  </ul>
                  <p><strong>7. 工具/环境问题</strong>：编程语言特性、编译器、IDE、提交系统等非算法本身的细节问题；</p>
                  <ul>
                    <li>【熟悉】了解常用语言特性、库函数用法</li>
                    <li>【检查】留意编译警告，熟悉评测环境</li>
                    <li>【记录】记录常见坑点</li>
                  </ul>
                  <p><strong>8. 心理性错误</strong>：受情绪、心态、比赛压力等因素影响导致的技术失误；</p>
                  <ul>
                    <li>【训练】模拟赛中培养抗压能力</li>
                    <li>【调整】保持积极心态，学会自我鼓励</li>
                    <li>【暂停】遇到困难时，短暂休息，深呼吸</li>
                  </ul>
                  <p><strong>9. 其他/未分类错误</strong>：暂时难以明确归类，或属特定情况的错误；</p>
                  <ul>
                    <li>【分析】尝试将其归入现有分类</li>
                    <li>【记录】暂存，等待进一步研究或求助</li>
                  </ul>
                </div>
              </div>
              <i class="el-icon-question" style="cursor: pointer; margin-left: 6px; font-size: 14px; color: #606266;"></i>
            </el-tooltip>
          </div>
        </template>
        <template slot-scope="scope">
          <div class="error-tag-list">
            <template v-if="scope.row.question_score === 100">
              <span style="color:#909399;">-</span>
            </template>
            <template v-else>
              <el-tag
                v-for="err in (scope.row.errors || [])"
                :key="err.error_code"
                :style="getErrorOutlineStyle(err.error_code)"
                class="error-outline-tag">
                <i class="el-icon-warning" style="margin-right: 4px;"></i>{{ err.error_name }}
              </el-tag>
            </template>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        label="标签"
        min-width="140"
        align="left">
        <template slot-scope="scope">
          <div class="tag-list">
            <template v-if="scope.row.tags && scope.row.tags.length > 0">
              <el-tag
                v-for="tag in scope.row.tags"
                :key="tag.tag_code"
                :class="getTagColorClass(tag.tag_code)"
                class="solid-tag">
                {{ tag.tag_name }}
              </el-tag>
            </template>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        :width="170"
        align="center"
        fixed="right"
        class-name="operation-column">
        <template slot-scope="scope">
          <div class="operation-buttons">
            <el-tooltip content="编辑" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button edit-button"
                @click.stop="handleEdit(scope.row)">
                <i class="el-icon-edit"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip content="订正" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button"
                :class="scope.row.question_score_fix === 100 ? 'disabled-button' : 'fix-button'"
                :disabled="scope.row.question_score_fix === 100"
                @click.stop="handleFix(scope.row)">
                <i class="el-icon-check"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip content="删除" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button delete-button"
                @click.stop="handleDelete(scope.row)">
                <i class="el-icon-delete"></i>
              </el-button>
            </el-tooltip>
            
            <el-tooltip :content="scope.row.is_collect ? '取消收藏' : '收藏'" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button"
                :class="scope.row.is_collect ? 'collect-active-button' : 'collect-button'"
                @click.stop="handleCollect(scope.row)">
                <i :class="{'el-icon-star-off': !scope.row.is_collect, 'el-icon-star-on': scope.row.is_collect}">
                </i>
              </el-button>
            </el-tooltip>

            <el-tooltip :content="getHangupTooltip(scope.row)" placement="top" :enterable="false" :open-delay="100">
              <el-button
                type="text"
                class="operation-button"
                :class="getHangupButtonClass(scope.row)"
                :disabled="scope.row.question_score === 100 || scope.row.question_score_fix === 100"
                @click.stop="handlePending(scope.row)">
                <i class="el-icon-video-pause">
                </i>
              </el-button>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <question-edit-dialog
      :visible.sync="editDialogVisible"
      :game-code="gameCode"
      :is-edit="!!currentQuestion"
      :question="currentQuestion"
      @success="handleEditSuccess"
      @cancel="handleEditCancel">
    </question-edit-dialog>
  </div>
</template>

<script>
import { getQuestions, updateQuestionCollect } from '@/api/question'
import QuestionEditDialog from './QuestionDialog.vue'
import request from '@/utils/request'
import { QUESTION_LEVELS, getQuestionLevelStyle } from '@/constants/questionLevel'
import { getErrorTagInlineStyle, getErrorOutlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionList',
  components: {
    QuestionEditDialog
  },
  props: {
    gameCode: {
      type: String,
      required: true
    },
    operationWidth: {
      type: Number,
      default: 170
    }
  },
  watch: {
    gameCode: {
      handler(val) {
        if (val) {
          this.refresh()
        }
      },
      immediate: true
    },
    questions: {
      handler(newQuestions) {
        console.log('题目数据已更新:', newQuestions)
        if (Array.isArray(newQuestions)) {
          this.localQuestions = [...newQuestions]
        }
      },
      immediate: true,
      deep: true
    }
  },
  data() {
    return {
      loading: false,
      localQuestions: [],
      editDialogVisible: false,
      currentQuestion: null
    }
  },
  methods: {
    async refresh() {
      try {
        this.loading = true
        const res = await getQuestions(this.gameCode)
        if (res.code === 200) {
          this.localQuestions = res.data || []
        } else {
          throw new Error(res.message || '获取题目列表失败')
        }
      } catch (error) {
        console.error('获取题目列表失败:', error)
        this.$message.error(error.message || '获取题目列表失败')
      } finally {
        this.loading = false
      }
    },
    
    getTagColorClass(tagCode) {
      const colors = [
        'tag-color-1', 'tag-color-2', 'tag-color-3', 'tag-color-4', 'tag-color-5',
        'tag-color-6', 'tag-color-7', 'tag-color-8', 'tag-color-9', 'tag-color-10'
      ]
      const index = parseInt(tagCode.split('_')[1], 36) % colors.length
      return colors[index]
    },
    
    async handleEdit(question) {
      try {
        this.currentQuestion = { ...question }
        this.editDialogVisible = true
      } catch (error) {
        console.error('编辑题目失败:', error)
        this.$message.error('编辑题目失败')
      }
    },
    
    async handleEditSuccess(question) {
      try {
        console.log('Question edited successfully:', question)
        this.editDialogVisible = false
        this.currentQuestion = null
        this.$emit('question-operation-complete', {
          type: 'edit',
          gameCode: this.gameCode,
          questionCode: question.question_code
        })
      } catch (error) {
        console.error('处理编辑完成失败:', error)
        this.$message.error('刷新数据失败')
      }
    },
    
    handleEditCancel() {
      this.editDialogVisible = false
      this.currentQuestion = null
    },
    
    async handleDelete(question) {
      try {
        await this.$confirm('确认删除该题目吗？', '提示', {
          type: 'warning'
        })
        
        const res = await request({
          url: `/api/game/question/${question.question_code}`,
          method: 'delete'
        })
        
        if (res.code === 200) {
          this.$message.success('删除成功')
          this.$emit('question-operation-complete', {
            type: 'delete',
            gameCode: this.gameCode,
            questionCode: question.question_code
          })
        } else {
          throw new Error(res.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Failed to delete question:', error)
          this.$message.error(error.message || '删除失败')
        }
      }
    },
    
    async handleFix(question) {
      try {
        await this.$confirm('确认将该题目订正分数改为100分吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false
        })
        
        const res = await request({
          url: `/api/game/question/${question.question_code}/fix`,
          method: 'put'
        })
        
        if (res.code === 200) {
          this.$message.success('订正成功')
          this.$emit('question-operation-complete', {
            type: 'fix',
            gameCode: this.gameCode,
            questionCode: question.question_code
          })
        } else {
          throw new Error(res.message || '订正失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Failed to fix question:', error)
          this.$message.error(error.message || '订正失败')
        }
      }
    },
    
    getQuestionLevelStyle,
    getErrorTagInlineStyle,
    getErrorOutlineStyle,
    getErrorTagInlineStyle,
    getQuestionLevelLabel(level) {
      const levelConfig = QUESTION_LEVELS.find(item => item.value === level)
      return levelConfig ? levelConfig.label : '未定义'
    },
    
    initColumns() {
      this.columns = [
        // ... other columns ...
        {
          label: '操作',
          fixed: 'right',
          width: this.operationWidth,
          render: (h, params) => {
            // ... existing render code ...
          }
        }
      ]
    },
    
    async handleCollect(row) {
      try {
        await this.$confirm(
          row.is_collect ? '确认取消收藏该题目吗？' : '确认收藏该题目吗？',
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
            closeOnClickModal: false,
            closeOnPressEscape: false,
            showClose: false
          }
        )

        const res = await updateQuestionCollect(row.question_code, !row.is_collect)
        if (res.code === 200) {
          // 更新本地状态
          row.is_collect = !row.is_collect
          this.$message.success(row.is_collect ? '收藏成功' : '取消收藏成功')
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('收藏操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    
    async handlePending(row) {
      try {
        // 判断题目得分和订正分数
        if (row.question_score === 100 || row.question_score_fix === 100) {
          this.$message.warning('该题目你已经' + (row.question_score === 100 ? 'AC' : '订正') + '，无法挂起。')
          return
        }

        await this.$confirm(row.is_pending ? '确认取消挂起该题目吗？' : '确认挂起该题目吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          showClose: false
        })
        
        const res = await request({
          url: `/api/question/${row.question_code}/pending`,
          method: 'put',
          data: {
            is_pending: !row.is_pending
          }
        })
        
        if (res.code === 200) {
          // 更新本地状态
          row.is_pending = !row.is_pending
          this.$message.success(row.is_pending ? '挂起成功' : '取消挂起成功')
          // 触发数据更新
          this.$emit('question-operation-complete', {
            type: 'pending',
            gameCode: this.gameCode,
            questionCode: row.question_code
          })
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('挂起操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },

    getScoreColor(score) {
      if (score === undefined || score === null) return '#606266';
      const percentage = (score / 100) * 100;
      if (percentage >= 100) return '#237804'; // 绿色
      if (percentage >= 75) return '#52C41A'; // 黄绿色
      if (percentage >= 50) return '#F7BA2A'; // 黄色
      if (percentage >= 25) return '#E6A23C'; // 橙色
      return '#F56C6C'; // 红色
    },

    getHangupTooltip(row) {
      if (row.question_score === 100) {
        return '已AC，无需挂起'
      }
      if (row.question_score_fix === 100) {
        return '已订正，无需挂起'
      }
      return row.is_pending ? '取消挂起' : '挂起'
    },

    getHangupButtonClass(row) {
      if (row.question_score === 100 || row.question_score_fix === 100) {
        return 'disabled-button'
      }
      return row.is_pending ? 'pending-active-button' : 'pending-button'
    }
  }
}
</script>

<style lang="scss" scoped>
.question-list {
  padding: 20px 0 20px 0;
  background-color: transparent;
  position: relative;
  width: 95%;
  margin: 0 0 0 auto;
  display: flex;
  flex-direction: column;
  
  .inner-table {
    flex: 1;
    width: 100% !important;
    margin: 0;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    overflow: auto;
    
    ::v-deep {
      .el-table__fixed-right {
        height: 100% !important;
        position: absolute;
        top: 0;
        right: 0;
        background-color: #fff;
        box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
      }
      
      .el-scrollbar__wrap {
        margin-right: 0 !important;
      }
      
      .el-table__body-wrapper {
        .el-scrollbar__wrap {
          margin-bottom: 0 !important;
        }
      }
    }
  }
  
  .question-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;

    .question-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .name-link {
      color: #409EFF;
      text-decoration: none;
      &:hover { text-decoration: underline; }
    }
  }
  
  .score-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    
    .el-icon-medal-1 {
      color: #006400; // 深绿色
      font-size: 16px;
    }
  }
  
  .score-perfect {
    color: #006400; // 深绿色
    font-weight: 600;
  }
  
  .score-warning {
    color: #F56C6C;
    font-weight: 500;
  }
  
  /* 题目难度样式 */
  ::v-deep .el-table__body .el-table__row .el-table__cell {
    .el-table__cell {
      height: 28px !important;
      line-height: 28px !important;
    }
  }
  
  .tag-color-1 { background-color: #409EFF !important; border-color: #409EFF !important; }  // Element UI 主蓝色
  .tag-color-2 { background-color: #67C23A !important; border-color: #67C23A !important; }  // Element UI 成功绿色
  .tag-color-3 { background-color: #E6A23C !important; border-color: #E6A23C !important; }  // Element UI 警告黄色
  .tag-color-4 { background-color: #F56C6C !important; border-color: #F56C6C !important; }  // Element UI 危险红色
  .tag-color-5 { background-color: #8E44AD !important; border-color: #8E44AD !important; }  // 深紫色
  .tag-color-6 { background-color: #16A085 !important; border-color: #16A085 !important; }  // 深青色
  .tag-color-7 { background-color: #D35400 !important; border-color: #D35400 !important; }  // 深橙色
  .tag-color-8 { background-color: #2980B9 !important; border-color: #2980B9 !important; }  // 深蓝色
  .tag-color-9 { background-color: #27AE60 !important; border-color: #27AE60 !important; }  // 深绿色
  .tag-color-10 { background-color: #C0392B !important; border-color: #C0392B !important; } // 深红色
  
  /* 仅标签列内的 tag 使用白字与实心背景 */
  .tag-list :deep(.el-tag) {
    margin-right: 5px;
    margin-bottom: 5px;
    border: none;
    color: white !important;
  }
  .tag-list :deep(.el-tag.tag-color-1) { background-color: #409EFF !important; border-color: #409EFF !important; }
  .tag-list :deep(.el-tag.tag-color-2) { background-color: #67C23A !important; border-color: #67C23A !important; }
  .tag-list :deep(.el-tag.tag-color-3) { background-color: #E6A23C !important; border-color: #E6A23C !important; }
  .tag-list :deep(.el-tag.tag-color-4) { background-color: #F56C6C !important; border-color: #F56C6C !important; }
  .tag-list :deep(.el-tag.tag-color-5) { background-color: #8E44AD !important; border-color: #8E44AD !important; }
  .tag-list :deep(.el-tag.tag-color-6) { background-color: #16A085 !important; border-color: #16A085 !important; }
  .tag-list :deep(.el-tag.tag-color-7) { background-color: #D35400 !important; border-color: #D35400 !important; }
  .tag-list :deep(.el-tag.tag-color-8) { background-color: #2980B9 !important; border-color: #2980B9 !important; }
  .tag-list :deep(.el-tag.tag-color-9) { background-color: #27AE60 !important; border-color: #27AE60 !important; }
  .tag-list :deep(.el-tag.tag-color-10) { background-color: #C0392B !important; border-color: #C0392B !important; }
}

/* 覆盖错误类型为描边/空心，不受上面白字样式影响 */
.error-tag-list :deep(.el-tag) {
  /* 由内联样式 getErrorOutlineStyle 提供具体颜色，这里不再覆盖 color */
  background-color: transparent !important;
  border-width: 1px !important;
}

/* 细化两类标签的默认间距，避免行高不一致 */
.error-outline-tag {
  margin-right: 4px !important;
  margin-bottom: 4px !important;
  height: 28px !important;
  line-height: 26px !important;
}
.solid-tag {
  margin-right: 4px !important;
  margin-bottom: 4px !important;
  height: 28px !important;
  line-height: 26px !important;
}

.operation-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  .operation-button {
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 4px;
    margin: 0;
    border: none;
    transition: all 0.2s ease;
    
    i {
      font-size: 14px;
      color: white;
    }
    
    // 编辑按钮 - 深蓝色
    &.edit-button {
      background-color: #0056b3;
      
      &:hover {
        background-color: #004494;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #003d82;
      }
    }
    
    // 订正按钮 - 绿色
    &.fix-button {
      background-color: #67C23A;
      
      &:hover {
        background-color: #5daf34;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #529b2e;
      }
    }
    
    // 删除按钮 - 红色
    &.delete-button {
      background-color: #F56C6C;
      
      &:hover {
        background-color: #dd5858;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #c54848;
      }
    }
    
    // 收藏按钮（未收藏状态）- 蓝色
    &.collect-button {
      background-color: #409EFF;
      
      &:hover {
        background-color: #337ecc;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #2b6cb0;
      }
    }
    
    // 收藏按钮（已收藏状态）- 黄色
    &.collect-active-button {
      background-color: #F7BA2A;
      
      &:hover {
        background-color: #e6a723;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #d4951c;
      }
    }
    
    // 挂起按钮（未挂起状态）- 蓝色
    &.pending-button {
      background-color: #409EFF;
      
      &:hover {
        background-color: #337ecc;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #2b6cb0;
      }
    }
    
    // 挂起按钮（已挂起状态）- 橙色
    &.pending-active-button {
      background-color: #E6A23C;
      
      &:hover {
        background-color: #d49134;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      &:active {
        transform: translateY(0);
        background-color: #c08025;
      }
    }
    
    // 禁用状态按钮
    &.disabled-button {
      background-color: #F2F3F5;
      cursor: not-allowed;
      opacity: 0.6;
      
      i {
        color: #C0C4CC;
      }
      
      &:hover {
        background-color: #F2F3F5;
        transform: none;
        box-shadow: none;
      }
    }
    
    &:disabled {
      cursor: not-allowed;
      background-color: #F2F3F5;
      opacity: 0.6;
      
      i {
        color: #C0C4CC;
      }
      
      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }
}

.error-tag-list,
.tag-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
</style>

<!-- 添加全局样式用于tooltip -->
<style>
.difficulty-tooltip {
  max-width: 600px !important;
}

.difficulty-tooltip-content {
  max-height: none;
  overflow-y: visible;
  padding: 0;
}

.difficulty-tooltip-content .difficulty-description {
  line-height: 1.6;
  font-size: 13px;
}

.difficulty-tooltip-content .difficulty-description p {
  margin: 8px 0;
  color: #ffffff;
}

.difficulty-tooltip-content .difficulty-description p strong {
  color: #409EFF;
  font-weight: 600;
}

.difficulty-tooltip-content .difficulty-description ul {
  margin: 4px 0 8px 0;
  padding-left: 16px;
}

.difficulty-tooltip-content .difficulty-description li {
  margin: 2px 0;
  color: #e6e6e6;
  font-size: 12px;
}

/* 操作列背景样式 */
::v-deep .operation-column .cell {
  background-color: rgba(248, 249, 250, 0.6) !important;
  padding: 8px 4px !important;
}
</style> 